import {USG_STATE} from "@wesstron/utils/Api/constants/usgStates";
import alasql from "alasql";
import i18next from "i18next";
import {
    filter,
    find,
    findLast,
    get,
    groupBy,
    has,
    isNil,
    memoize,
} from "lodash";
import moment from "moment";
import "moment-timezone";
import "moment/locale/pl";
import {UnitTypes} from "../constans/unitTypes";
import animalsDB from "../database/animalsDB";
import eventsDB from "../database/eventsDB";
import lokiDB from "../database/lokiDB";
import {preEvents} from "../utils/AnimalDocumentsUtils";
import {
    calculateAnimalParametersForAllAnimals,
    eventHierarchy,
} from "../utils/AnimalParamsWorkerUtils";
import {
    calculateAnimalParameters,
    calculateTechnologyGroupParametersForAnimal,
} from "../utils/AnimalParamUtils";
import {localMomentToUTC} from "../utils/DateTimeUtils";
import {calculateGroupParameters} from "../utils/GroupParamUtils";
import {
    getAnimalTimespanInGroupByAllEvents,
    isSubgroup,
} from "../utils/GroupUtils";
import {i18nOptions} from "../utils/I18nUtils";
import {isBetween, isFiniteNumber} from "../utils/MathUtils";
import {isMobile} from "../utils/MobileUtils";
import {convertWeightUnitTo} from "../utils/UnitUtils";
import {createKeyToValueDictionary} from "../utils/Utils";
import {removeReasonCodeToTranslationKey} from "../views/new-farm-view/tabs/results-renderers/ResultsReportFormatters";
// import {compressData, decompressData} from "../utils/GZipUtils";
// import Loki from "lokijs";
// import IncrementalIndexedDBAdapter from "lokijs/src/incremental-indexeddb-adapter";

let i18n;
// let db;
// let parametersCollection;

if (process.env.NODE_ENV === "production") {
    const methods = ["log", "debug", "warn", "info", "time", "timeEnd"];
    for (let i = 0; i < methods.length; i++) {
        console[methods[i]] = function () {};
    }
}

function postMessage(id, data) {
    const message = {id, data};
    const array = new TextEncoder().encode(JSON.stringify(message));
    self.postMessage(array.buffer, [array.buffer]);
}

function prepareTable() {
    alasql(`CREATE TABLE IF NOT EXISTS ParamsTable`);
}

const createAggregators = () => {
    alasql.aggr.LAST_NOT_NULL = (value, accumulator, stage) => {
        switch (stage) {
            case 1: {
                if (isNil(value)) return null;
                return value;
            }
            case 2: {
                if (isNil(value)) return accumulator;
                return value;
            }
            default: {
                return accumulator ?? null;
            }
        }
    };
    alasql.aggr.FIRST_NOT_NULL = (value, accumulator, stage) => {
        switch (stage) {
            case 1: {
                if (isNil(value)) return null;
                return value;
            }
            case 2: {
                if (isNil(value)) return accumulator;
                return isNil(accumulator) ? value : accumulator;
            }
            default: {
                return accumulator ?? null;
            }
        }
    };
    alasql.aggr.ARRAY_NOT_NULL = (value, accumulator, stage) => {
        switch (stage) {
            case 1: {
                if (isNil(value)) return [];
                return [value];
            }
            case 2: {
                if (isNil(value)) return accumulator;
                return accumulator.concat([value]);
            }
            default: {
                return accumulator ?? [];
            }
        }
    };
};

const createCustomFunctions = ({dictionaries, buildingsMap, constData}) => {
    console.log("CREATING CUSTOM FUNCTIONS");
    const hasValue = (o) => "any" !== o && "*" !== o;
    // indices are made and stored inside alasql but I cant get alasql to use them, so we must extract them manually
    // (this could be due to "cheat-y" way of inserting tables - couldn't be bothered to investigate more)
    const getIndexedData = (tableName, indexName, ...args) => {
        // find unique index name (these are numeric as of 10.01.2024)
        const index =
            alasql.tables[tableName].inddefs[indexName].hh ?? "unknown";
        // internal alasql keys are made this way
        const key = args.filter((o) => hasValue(o)).join("`");
        return alasql.tables[tableName].indices[index][key] ?? [];
    };

    const getIndexKey = memoize(
        (hasAnmID, hasGroup, hasCycle) => {
            const key = [];
            if (hasAnmID) key.push("AnmID");
            if (hasGroup) key.push("group_nr");
            if (hasCycle) key.push("cycle");
            key.push("index");
            const index = key.join("_");
            console.log("possible index %s", index);
            return index;
        },
        (...args) => args.join("_")
    );
    const makeCheckCondition = (condition, timestamp) => {
        switch (condition.replace("last ", "")) {
            case "<=":
                return (ts) => ts <= timestamp;
            case "<":
                return (ts) => ts < timestamp;
            case "=":
                return (ts) => ts === timestamp;
            case ">=":
                return (ts) => ts >= timestamp;
            case ">":
                return (ts) => ts > timestamp;
            case "*":
            case "any":
            default:
                return () => true;
        }
    };
    const getBuildingsHierarchy = memoize((PlcmntID) => {
        const hierarchy = {};
        let level = buildingsMap.get(PlcmntID);
        if (!level) return {};
        let parent = level.id !== level.parentId ? level.parentId : null;
        hierarchy[level.level] = PlcmntID;
        while (parent) {
            const tmp = buildingsMap.get(parent);
            hierarchy[tmp.level] = parent;
            parent = tmp.id !== tmp.parentId ? tmp.parentId : null;
        }
        return hierarchy;
    });
    /**
     * couldn't get alasql indices to work with a simple query
     * this finds a parameter with given name inside current animal cycle and its quite fast
     * @deprecated
     * @param key
     * @param condition {'last <='|'last <'|'last ='|'last >='|'last >'|'last *'|'last any'|'<='|'<'|'='|'>='|'>'|'*'|'any'}
     * @param timestamp
     * @param animalId {string|"any"|"*"} any można użyć tylko tak jak pozwala na to lokalny index tzn. ["cycle"], ["group_nr", "AnmID"], ["group_nr", "AnmID", "cycle"]
     * @param cycle {number|"any"|"*"}
     * @param group_nr {number|"any"|"*"}
     * @return {*}
     * @constructor
     */
    alasql.fn.FIND_ANIMAL_PARAM = (
        key,
        condition,
        timestamp,
        animalId,
        cycle,
        group_nr
    ) => {
        // todo: zoptymalizować przeszukiwanie danych
        const checkCondition = makeCheckCondition(condition, timestamp);
        const findFn = condition.startsWith("last") ? findLast : find;
        const idxKey = getIndexKey(
            hasValue(animalId),
            hasValue(group_nr),
            hasValue(cycle)
        );
        const data = getIndexedData(
            "ParamsTable",
            idxKey,
            animalId,
            group_nr,
            cycle
        );
        return findFn(
            data,
            (r) => (key === "any" || r.key === key) && checkCondition(r.dataEv)
        );
    };
    alasql.fn.FILTER_ANIMAL_PARAM = (
        key,
        condition,
        timestamp,
        animalId,
        cycle,
        group_nr
    ) => {
        const checkCondition = makeCheckCondition(condition, timestamp);
        const idxKey = getIndexKey(
            hasValue(animalId),
            hasValue(group_nr),
            hasValue(cycle)
        );
        const data = getIndexedData(
            "ParamsTable",
            idxKey,
            animalId,
            group_nr,
            cycle
        );
        return filter(
            data,
            (r) => (key === "any" || r.key === key) && checkCondition(r.dataEv)
        );
    };
    /**
     * zwraca stała wartość
     * @param key
     * @returns {null|*}
     * @constructor
     */
    alasql.fn.CONST = (key) => {
        switch (key) {
            // jezeli trafiles tutaj i musisz cos dodac to patrz plik ParamSynchronizer
            case "FIRST_CYCLE_INDEX":
                return constData.firstCycleIndex;
            case "TIME_FROM_INSEMINATION_TO_PARTURITION":
                return constData.timeFromInseminationToParturition;
            case "TIME_FROM_INSEMINATION_TO_PREGNANCY":
                return constData.timeFromInseminationToPregnancy;
            default:
                return null;
        }
    };
    alasql.fn.TRANS = memoize((key) => {
        return i18n.t(key);
    });
    /**
     * zwraca wartość domyślną
     * przydatne, gdy chcemy wyzerować nulle
     * albo zrobić alias dla kolumny
     * @param value
     * @param defaultValue
     * @returns {any|null}
     * @constructor
     */
    alasql.fn.DEF = (value, defaultValue) => {
        if (isNil(value)) return defaultValue ?? null;
        return value;
    };
    alasql.fn.GET = (value, key, defaultValue) => {
        return get(value, key, defaultValue);
    };
    /**
     * odpowiednik toFixed, zwraca null jesli value nie jest liczbą
     * @param value
     * @param fractionDigits
     * @returns {null|number}
     * @constructor
     */
    alasql.fn.FIXED = (value, fractionDigits = 2) => {
        if (!isFiniteNumber(value)) return null;
        return +value.toFixed(fractionDigits);
    };
    const fmtDateUtils = {
        momentInstance: moment(),
    };
    fmtDateUtils.gradation = {
        month: (monthNumber) =>
            fmtDateUtils.momentInstance.month(monthNumber).format("MMMM"),
        weekDay: (dayNumber) =>
            fmtDateUtils.momentInstance.isoWeekday(dayNumber).format("dddd"),
    };
    /**
     * formatowanie daty
     * todo: dodac utc
     * @param value
     * @param gradation {string|null}
     * @returns {string|null}
     * @constructor
     */
    alasql.fn.FMT_DATE = (value, gradation) => {
        if (isNil(value)) return null;
        return gradation
            ? fmtDateUtils.gradation[gradation](value)
            : moment(value).format("L");
    };
    /**
     * pierwsze 3 argumenty kompatybilne z 'DATEDIFF' z T-SQL
     * @param datePart
     * @param startDate
     * @param endDate
     * @param startOf {"day"|null}
     * @returns {number|null}
     * @constructor
     */
    alasql.fn.DATE_DIFF = (datePart, startDate, endDate, startOf = null) => {
        if (isNil(startDate) || isNil(endDate)) return null;
        const start = startOf
            ? moment(startDate).startOf(startOf)
            : moment(startDate);
        const end = startOf
            ? moment(endDate).startOf(startOf)
            : moment(endDate);
        return end.diff(start, datePart);
    };
    alasql.fn.DATE_DIFF_UTC = (
        datePart,
        startDate,
        endDate,
        startOf = null
    ) => {
        if (isNil(startDate) || isNil(endDate)) return null;
        const start = startOf
            ? moment.utc(startDate).startOf(startOf)
            : moment.utc(startDate);
        const end = startOf
            ? moment.utc(endDate).startOf(startOf)
            : moment.utc(endDate);
        return end.diff(start, datePart);
    };
    /**
     * returns a pretty name for given format and value
     * @param format {string|"technologyGroupRemoval"|"weight"|"usgResult"|"devices"|"placements"|"animals"|"employees"|"dictionaries"|"groups"|"settings"|"sales"|"medicines"} nazwa formatowania zgodna z getKeyValueLogDict
     * @param value {string|number}
     * @returns {string|number|string|*}
     * @constructor
     */
    alasql.fn.FMT = (format, value) => {
        switch (format) {
            case "technologyGroupRemoval": {
                const translation = removeReasonCodeToTranslationKey(value);
                return translation ? i18n.t(translation) : null;
            }
            case "usgResult": {
                switch (value) {
                    case USG_STATE.NEGATIVE:
                        return i18n.t("events.usgEvent.negative");
                    case USG_STATE.POSITIVE:
                        return i18n.t("events.usgEvent.positive");
                    case USG_STATE.REPEAT:
                        return i18n.t("events.usgEvent.toRepeat");
                    case USG_STATE.MISSING:
                        return i18n.t("events.usgEvent.missing");
                    default:
                        return null;
                }
            }
            case "weight": {
                return convertWeightUnitTo(value, {
                    rawValue: true,
                    acceptNil: true,
                    fixed: 4,
                    unit: UnitTypes.MEDIUM,
                });
            }
            case "retentionReason": {
                switch (value) {
                    case 0:
                        return i18n.t("noNextInsemination");
                    case 1:
                        return i18n.t("sale");
                    case 2:
                        return i18n.t("fall");
                    case 3:
                        return i18n.t(
                            "animalDocuments.removeReason.repetition"
                        );
                    default:
                        return value;
                }
            }
            default: {
                if (has(dictionaries, format)) {
                    return get(dictionaries, format)?.[value] ?? value;
                }
                break;
            }
        }
        return value;
    };
    alasql.fn.GENERATE_DATES = (startDate, endDate) => {
        const dates = [];
        let date = localMomentToUTC(moment(startDate));
        const end = localMomentToUTC(
            moment(endDate === null ? undefined : endDate).endOf("day")
        );
        while (date <= end) {
            dates.push({date: +date});
            date = date.add(1, "d");
        }
        return dates;
    };
    alasql.fn.GET_LEVEL = (PlcmntID, level) => {
        const hierarchy = getBuildingsHierarchy(PlcmntID);
        return hierarchy[level] || null;
    };
};

prepareTable();
createAggregators();

async function initDatabase(data, shouldPostMessage = true) {
    const {
        id,
        data: {sub, user, isService, farmTimeZone, fullParametersCalc},
        // data: {farmTimeZone},
    } = data;
    moment.tz.setDefault(farmTimeZone);
    if (isMobile() && !fullParametersCalc) {
        if (shouldPostMessage) {
            postMessage(id, {error: null});
        }
        return;
    }
    // parametersCollection = null;
    let key = isService ? `${sub}_${user.ClientID}` : sub;
    await lokiDB.loadDatabase(key, true, false);
    // let newKey = "parameters_" + key + ".db";
    // if (process.env.PUBLIC_URL && process.env.PUBLIC_URL.length > 0) {
    //     newKey = `${process.env.PUBLIC_URL}-${newKey}`;
    // }
    // db = new Loki(newKey, {
    //     // autoload: true,
    //     // autoloadCallback: loadHandler,
    //     autosave: true,
    //     autosaveInterval: 10000, // 1 seconds
    //     adapter: new IncrementalIndexedDBAdapter(),
    //     verbose: true,
    //     serializationMethod: "normal",
    // });
    if (shouldPostMessage) {
        postMessage(id, {error: null});
    }
    // self.postMessage({id, data: {error: null}});
}

async function calculateParameters(data) {
    const {
        id,
        data: {
            FarmID,
            utilResults,
            buildingsMap: buildingsMapArray,
            fullCalc,
            animalChanges,
            reloadDatabase,
        },
    } = data;

    if (reloadDatabase) {
        await initDatabase(data, false);
        createAnimalTable(data, false);
        createEventsTable(data, false);
    }
    const buildingsMap = new Map(buildingsMapArray);
    // if (!parametersCollection) {
    //     parametersCollection = lokidb.getCollection("ParamsTable");
    //     parametersCollection = db.getCollection("ParamsTable");
    //     if (!parametersCollection) {
    //         parametersCollection = db.addCollection("ParamsTable", {
    //             // indices: [id], find nie dziala bo w pewnym momencie ilosc binaryIndicies nie zgadza sie z iloscia danych, tl;dr nie ma indicies nie ma problemu
    //             clone: false,
    //             cloneMethod: "parse-stringify",
    //             disableMeta: true,
    //         });
    //     } else {
    //         const element = parametersCollection.findOne();
    //         if (element) {
    //             alasql.tables.ParamsTable.data = decompressData(element.data);
    //         }
    //     }
    // }

    let shouldFullCalc = fullCalc;
    if (
        !alasql.tables.ParamsTable ||
        alasql.tables.ParamsTable.data.length === 0
    )
        shouldFullCalc = true;
    console.log("CALCULATE_PARAMETERS", shouldFullCalc, buildingsMap);
    let {parameters, animals, groups} = calculateAnimalParametersForAllAnimals(
        FarmID,
        utilResults,
        buildingsMap,
        animalChanges,
        shouldFullCalc
    );
    if (shouldFullCalc) {
        alasql.tables.ParamsTable.data = parameters;
    } else {
        if (animals.length > 0) {
            const res = alasql(`DELETE FROM ParamsTable WHERE AnmID IN @(?)`, [
                animals,
            ]);
            console.log("Removed animals", res);
        }
        if (groups.length > 0) {
            const res = alasql(
                `DELETE FROM ParamsTable WHERE group_nr IN @(?)`,
                [groups]
            );
            console.log("Removed groups", res);
        }
        alasql.tables.ParamsTable.data = [
            ...alasql.tables.ParamsTable.data,
            ...parameters,
        ];
    }
    alasql(`CREATE INDEX group_nr_index ON ParamsTable (group_nr)`);
    alasql(`CREATE INDEX AnmID_index ON ParamsTable (AnmID)`);
    alasql(`CREATE INDEX AnmID_cycle_index ON ParamsTable (AnmID, cycle)`);
    alasql(
        `CREATE INDEX AnmID_group_nr_cycle_index ON ParamsTable (AnmID, group_nr, cycle)`
    );
    postMessage(id, {error: null});
    // self.postMessage({id, data: {error: null}});
    // new Promise(async (resolve) => {
    //     parametersCollection.clear({removeIndices: true});
    //     parametersCollection.insert({
    //         data: compressData(alasql.tables.ParamsTable.data),
    //     }); // dodawane jako jeden rekord, zeby latwiej usuwac $loki, w przyszlosci mozliwy gzip itp
    //     await lokidb.asyncSaveDB();
    //     resolve();
    // });
}

function callQuery(data) {
    const {
        id,
        data: {query, params},
    } = data;
    const result = alasql(query, params);
    postMessage(id, {result});
    // self.postMessage({id, data: {result}});
}

function _createCustomFunctions(data) {
    const {
        id,
        data: {dictionaries, buildingsMap: buildingsMapArray, constData},
    } = data;
    const buildingsMap = new Map(buildingsMapArray);
    createCustomFunctions({dictionaries, buildingsMap, constData});
    postMessage(id, {error: null});
    // self.postMessage({id, data: {error: null}});
}

function clearData(data) {
    const {id} = data;
    alasql.tables.ParamsTable.data = [];
    postMessage(id, {error: null});
    // self.postMessage({id, data: {error: null}});
}

function createAnimalTable(data, shouldPostMessage = true) {
    const {
        id,
        data: {FarmID},
    } = data;
    alasql("CREATE TABLE IF NOT EXISTS AnimalsTable");
    const animals = animalsDB.getAllAnimals(
        FarmID,
        undefined,
        true,
        false,
        true
    );
    alasql.tables.AnimalsTable.data = animals;
    if (shouldPostMessage) {
        postMessage(id, {error: null});
    }
    // self.postMessage({id, data: {error: null}});
}

function createEventsTable(data, shouldPostMessage = true) {
    const {
        id,
        data: {FarmID},
    } = data;
    alasql("CREATE TABLE IF NOT EXISTS EventsTable");
    const events = eventsDB.getAllEventsForFarm(FarmID);
    alasql.tables.EventsTable.data = events;
    if (shouldPostMessage) {
        postMessage(id, {error: null});
    }
    // self.postMessage({id, data: {error: null}});
}

async function changeLanguage(data) {
    const {
        id,
        data: {language, resource},
    } = data;
    console.log(id, language, resource);
    if (!i18n) {
        i18n = i18next.createInstance({
            ...i18nOptions,
            lng: language,
            defaultNS: "translation",
            ns: [],
        });
        await i18n.init();
    }
    i18n.changeLanguage(language);
    for (let key in resource) {
        i18n.addResourceBundle(language, key, resource[key], true, true);
    }
    postMessage(id, {error: null});
    // self.postMessage({id, data: {error: null}});
}

function calculateOneAnimal(data) {
    const {
        id,
        data: {
            animal,
            events,
            buildingsMap: buildingsMapArray,
            utilResults,
            groups,
        },
    } = data;
    const buildingsMap = new Map(buildingsMapArray);
    const cycles = preEvents(events, animal, false, utilResults);
    const result = calculateAnimalParameters(
        animal,
        events,
        cycles.cycleTable,
        utilResults,
        buildingsMap,
        groups
    );
    alasql.tables.ParamsTable.data = result;
    postMessage(id, {error: null});
    // self.postMessage({id, data: {error: null}});
}

function calculateOneGroup(data) {
    const {
        id,
        data: {
            group,
            animals,
            events,
            buildingsMap: buildingsMapArray,
            utilResults,
        },
    } = data;
    const buildingsMap = new Map(buildingsMapArray);
    const animalDict = createKeyToValueDictionary(animals, "AnmID");
    const isAnimalEligibleForTimespan = memoize((AnmID) => {
        const animal = animalDict[AnmID];
        if (!animal) return false;
        return !isSubgroup(animal);
    });

    events.sort((a, b) => {
        const diff = a.EvTime - b.EvTime;
        if (diff !== 0) return diff;
        return (
            (eventHierarchy[a.EvCode] || eventHierarchy.Other) -
            (eventHierarchy[b.EvCode] || eventHierarchy.Other)
        );
    });
    const animalTimespan = getAnimalTimespanInGroupByAllEvents(
        events,
        group.AnmGrp,
        {
            isAnimalSupported: isAnimalEligibleForTimespan,
        }
    );
    let groupEvents = events.filter((ev) => {
        if (!animalTimespan[ev.AnmID]) return true;
        return animalTimespan[ev.AnmID].some((range) =>
            isBetween(ev.EvTime, range[0], range[1])
        );
    });
    const result = calculateGroupParameters(
        group,
        animals,
        groupEvents,
        buildingsMap,
        utilResults
    );
    alasql.tables.ParamsTable.data = result;
    console.log("CALCULATE_GROUP", result);
    postMessage(id, {error: null});
    // self.postMessage({id, data: {error: null}});
}

function calculateTechnologyGroupParams(data) {
    const {
        id,
        data: {animals, events, buildingsMap: buildingsMapArray, utilResults},
    } = data;
    const buildingsMap = new Map(buildingsMapArray);
    const res = [];
    const groupedEvents = groupBy(events, "AnmID");
    for (let animal of animals) {
        const animalEvents = groupedEvents[animal.AnmID];
        const cycles = preEvents(animalEvents, animal, false, utilResults);
        const result = calculateTechnologyGroupParametersForAnimal(
            animal,
            animalEvents,
            cycles.cycleTable,
            utilResults,
            buildingsMap
        );
        res.push(...result);
    }
    alasql.tables.ParamsTable.data = res;
    console.log("calculateTechnologyGroupParams", res);
    postMessage(id, {error: null});
    // self.postMessage({id, data: {error: null}});
}

self.onmessage = async (event) => {
    console.log("results worker", event.data);
    let decoder = new TextDecoder("utf-8");
    let view = new DataView(event.data, 0, event.data.byteLength);
    let string = decoder.decode(view);
    let data = JSON.parse(string);
    try {
        console.log("results worker", data);
        const {type} = data;
        switch (type) {
            case "CALCULATE_PARAMETERS":
                await calculateParameters(data);
                break;
            case "CALL_QUERY":
                callQuery(data);
                break;
            case "CREATE_CUSTOM_FUNCTIONS":
                _createCustomFunctions(data);
                break;
            case "CLEAR_DATA":
                clearData(data);
                break;
            case "INIT_DATABASE":
                await initDatabase(data);
                break;
            case "CREATE_ANIMAL_TABLE":
                createAnimalTable(data);
                break;
            case "CREATE_EVENTS_TABLE":
                createEventsTable(data);
                break;
            case "CHANGE_LANGUAGE":
                await changeLanguage(data);
                break;
            case "CALCULATE_ANIMAL":
                calculateOneAnimal(data);
                break;
            case "CALCULATE_GROUP":
                calculateOneGroup(data);
                break;
            case "CALCULATE_TECHNOLOGY_GROUP":
                calculateTechnologyGroupParams(data);
                break;
            default:
                break;
        }
    } catch (e) {
        console.error("error results worker", e);
        postMessage(data.id, {error: true});
        // self.postMessage({error: true});
    }
};
